import type { ReactElement } from 'react';
import { createAnalytics } from '@fetmode/analytics';
import {
  CookieConsentProvider,
  DevPanel,
  VisitorProvider,
  resolveApiUrl,
  useCookieConsent,
  useVisitor,
} from '@fetmode/app-core';
import { t, LocaleProvider, useLocale } from '@fetmode/i18n';
import { defaultTheme, ThemeProvider } from '@fetmode/theme';
import { Button } from '@fetmode/ui';
import { useEffect } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AcceptAlphaInvitePage } from './features/auth/AcceptAlphaInvitePage.js';
import {
  AuthProvider,
  AuthModalProvider,
  LoginPage,
  ProtectedRoute,
  RegisterPage,
  TesterPanel,
  TesterPanelProvider,
  useAuth,
} from './features/auth/index.js';
import {
  EntryComplianceLayer,
  VerifyPage,
  VerifySessionPage,
} from './features/compliance/index.js';
import { ExperienceGuard, ExperienceOnboardingPage, TagsOnboardingPage, TurnOnsOnboardingPage, homePathForMode } from './features/experience/index.js';
import { CommunityHomePage } from './features/community/index.js';
import { HomePage, LandingPage } from './features/home/home-browse.js';
import './features/shows/show-room-desktop-dock.css';
import { InfoPage } from './features/info/index.js';
import { ProfilePage } from './features/profile/index.js';
import {
  LegacyVideoRedirect,
  LibraryPage,
  VideoDetailPage,
  VideosPage,
} from './features/videos/index.js';
import { SettingsPage } from './features/settings/index.js';
import { StatsPage } from './features/stats/index.js';
import { PurchaseConfirmPage, WalletPage, CoinShopProvider } from './features/wallet/index.js';
import { AppShell } from './shared/components/AppShell/index.js';

const apiBaseUrl = resolveApiUrl(import.meta.env);

const analytics = createAnalytics(import.meta.env.VITE_SEGMENT_WRITE_KEY, {
  app: 'consumer',
  environment: import.meta.env.MODE,
  locale: 'en',
});

function LegacyLiveRedirect(): ReactElement {
  const { username } = useParams<{ username: string }>();
  const handle = (username ?? '').replace(/^@/, '');
  return <Navigate to={handle ? `/@${handle}` : '/'} replace />;
}

function RootHome(): ReactElement {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.auth.loading')}</p>;
  }

  // Guests get the editorial landing + register form. Signed-in sessions
  // (fans, models, staff) get their mode home — never the register form.
  if (!user) {
    return <LandingPage />;
  }

  if (user.role === 'USER' && !user.experienceOnboardingCompleted) {
    return <Navigate to="/onboarding/experience" replace />;
  }

  const home = homePathForMode(user.primaryExperienceMode);

  if (home === '/community') {
    return (
      <ExperienceGuard>
        <CommunityHomePage />
      </ExperienceGuard>
    );
  }

  if (home === '/videos') {
    return (
      <ExperienceGuard>
        <VideosPage />
      </ExperienceGuard>
    );
  }

  return (
    <ExperienceGuard>
      <HomePage />
    </ExperienceGuard>
  );
}

function AppRoutes(): ReactElement {
  const { user } = useAuth();
  // Subscribe so shell/routes re-render when language changes (`t()` is module-scoped).
  useLocale();
  const { hasAnalyticsConsent } = useCookieConsent();
  const { visitor, kind } = useVisitor();

  useEffect(() => {
    if (!hasAnalyticsConsent) {
      return;
    }
    analytics.page('consumer.home.main', {
      visitor_id: visitor.visitorId,
      visitor_kind: kind,
      visit_count: visitor.visitCount,
    });
  }, [hasAnalyticsConsent, kind, visitor.visitCount, visitor.visitorId]);

  useEffect(() => {
    if (user && hasAnalyticsConsent) {
      analytics.identify(user.id, {
        role: user.role,
        locale: user.locale,
        visitor_id: visitor.visitorId,
        visitor_kind: kind,
      });
    }
  }, [hasAnalyticsConsent, kind, user, visitor.visitorId]);

  return (
    <AppShell>
      <Routes>
        <Route
          path="/onboarding/experience"
          element={
            <ProtectedRoute>
              <ExperienceOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/content"
          element={<Navigate to="/onboarding/turn-ons" replace />}
        />
        <Route
          path="/onboarding/turn-ons"
          element={
            <ProtectedRoute>
              <TurnOnsOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/tags"
          element={
            <ProtectedRoute>
              <TagsOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<RootHome />} />
        <Route
          path="/community"
          element={
            <ExperienceGuard>
              <CommunityHomePage />
            </ExperienceGuard>
          }
        />
        <Route
          path="/cams"
          element={
            <ExperienceGuard>
              <HomePage />
            </ExperienceGuard>
          }
        />
        {/* Legacy live-room URLs now forward to the model's profile page. */}
        <Route path="/live/:username" element={<LegacyLiveRedirect />} />
        <Route
          path="/videos"
          element={
            <ExperienceGuard>
              <VideosPage />
            </ExperienceGuard>
          }
        />
        {/* Legacy `/videos/:mediaId` (UUID) forwards to the creator-scoped URL. */}
        <Route path="/videos/:mediaId" element={<LegacyVideoRedirect />} />
        <Route
          path="/library"
          element={
            <ProtectedRoute>
              <ExperienceGuard>
                <LibraryPage />
              </ExperienceGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stats"
          element={
            <ProtectedRoute>
              <ExperienceGuard>
                <StatsPage />
              </ExperienceGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <ExperienceGuard>
                <SettingsPage />
              </ExperienceGuard>
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/accept-invite" element={<AcceptAlphaInvitePage />} />
        {/* Static info / legal / support pages linked from the footer. */}
        <Route path="/p/:slug" element={<InfoPage />} />
        <Route
          path="/verify/session/:sessionId"
          element={
            <ProtectedRoute>
              <VerifySessionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/verify"
          element={
            <ProtectedRoute>
              <VerifyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet/purchase/:purchaseId"
          element={
            <ProtectedRoute>
              <PurchaseConfirmPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <WalletPage />
            </ProtectedRoute>
          }
        />
        {/* Creator-scoped video page — /@creator/v/{slug}, no GUID in the URL. */}
        <Route
          path="/:username/v/:slug"
          element={
            <ExperienceGuard>
              <VideoDetailPage />
            </ExperienceGuard>
          }
        />
        {/* Every user's individual page. Resolves the account, then renders the
            model storefront/live room for creators or the community profile for
            members. `@` is carried in the link; static routes above still win
            via React Router specificity. */}
        <Route path="/:username" element={<ProfilePage />} />
        <Route
          path="*"
          element={
            <section>
              <h1>{t('common.home.welcome')}</h1>
              <Link to="/cams">
                <Button label="Back home" variant="ghost" />
              </Link>
            </section>
          }
        />
      </Routes>
    </AppShell>
  );
}

export function App(): ReactElement {
  return (
    <ThemeProvider theme={defaultTheme}>
      <BrowserRouter>
        <VisitorProvider>
          <AuthProvider apiBaseUrl={apiBaseUrl}>
            <LocaleProvider>
              <AuthModalProvider>
                <CookieConsentProvider>
                  <EntryComplianceLayer>
                    <CoinShopProvider>
                      <TesterPanelProvider>
                        <AppRoutes />
                        <TesterPanel variant="web" />
                      </TesterPanelProvider>
                      {import.meta.env.DEV ? <DevPanel /> : null}
                    </CoinShopProvider>
                  </EntryComplianceLayer>
                </CookieConsentProvider>
              </AuthModalProvider>
            </LocaleProvider>
          </AuthProvider>
        </VisitorProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
